import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpRight, ChevronRight, DollarSign, Flag, History, LogOut, Mail, Palette, PieChart, RotateCcw, ShieldAlert, Trash2, Type, X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Btn, Card, Confirm, Empty, Field, FilePickButton, IconBtn, Loading, Page, cx } from "../components/wf";
import { api, emailResultMessage, fileUrl, logoUrl, money, ROLE_LABELS, uploadFile } from "../lib/api";
import { useAuth, usePerms } from "../lib/auth";
import { DEFAULT_BRAND, DEFAULT_LABELS, DEFAULT_NAME, isValidHex, useBranding, useStatuses } from "../lib/queries";

function NavRow({ icon: Icon, title, subtitle, to, testid }) {
  return (
    <Link to={to} data-testid={testid} className="flex min-h-[68px] items-center gap-4 border-b border-wf-line px-5 py-3 transition-colors last:border-0 hover:bg-wf-s3">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-wf-soft text-wf-softink"><Icon size={19} /></span>
      <span className="min-w-0 flex-1"><span className="block font-semibold">{title}</span><span className="block truncate text-sm text-wf-mute">{subtitle}</span></span>
      <ChevronRight size={18} className="text-wf-line2" />
    </Link>
  );
}

function ReviewAccessRow() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["review-access"], queryFn: () => api("/settings/review-access") });
  if (!q.data?.available) return null;
  const toggle = async () => {
    try {
      const enabled = !q.data.enabled;
      qc.setQueryData(["review-access"], await api("/settings/review-access", { method: "PUT", body: { enabled } }));
      toast.success(enabled ? "Reviewer login turned on" : "Reviewer login turned off");
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <div className="flex min-h-[68px] items-center gap-4 border-b border-wf-line px-5 py-3" data-testid="review-access-row">
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-wf-soft text-wf-softink"><ShieldAlert size={19} /></span>
      <span className="min-w-0 flex-1"><span className="block font-semibold">Store Reviewer Login</span>
        <span className="block truncate text-sm text-wf-mute">{q.data.email} · {q.data.enabled ? "ON – turn off when the review is done" : "OFF"}</span></span>
      <button data-testid="review-access-switch" onClick={toggle} className={cx("relative h-7 w-12 rounded-full transition-colors", q.data.enabled ? "bg-wf-brand" : "bg-wf-line2")}>
        <span className={cx("absolute top-0.5 h-6 w-6 rounded-full bg-white shadow transition-transform", q.data.enabled ? "translate-x-[22px]" : "translate-x-0.5")} />
      </button>
    </div>
  );
}

export function Settings() {
  const { user, logout } = useAuth();
  const isOwner = user?.role === "admin";
  const branding = useBranding();
  const statuses = useStatuses();
  const perDiem = useQuery({ queryKey: ["per-diem"], queryFn: () => api("/settings/per-diem") });
  const platinum = useQuery({ queryKey: ["platinum"], queryFn: () => api("/settings/platinum"), enabled: isOwner });
  return (
    <Page title="Settings" testid="settings-page">
      <Card className="mb-8 flex items-center gap-4">
        <div className="min-w-0 flex-1"><p className="text-lg font-bold" data-testid="settings-user-name">{user?.name}</p><p className="text-sm text-wf-mute">{user?.email}</p></div>
        <span data-testid="settings-user-role" className="rounded-full bg-wf-soft px-3 py-1 text-xs font-bold text-wf-softink">{ROLE_LABELS[user?.role]}</span>
      </Card>
      <p className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-wf-mute">Manage</p>
      <div className="overflow-hidden rounded-2xl border border-wf-line bg-wf-s2">
        <NavRow icon={Palette} title="Company Branding" subtitle={`${branding.data?.company_name || DEFAULT_NAME} · logo, name & brand color`} to="/settings/branding" testid="settings-branding-button" />
        <NavRow icon={DollarSign} title="Per Diem Amount" subtitle={perDiem.data ? `${money(perDiem.data.amount)} per technician per day` : "Set the daily per diem"} to="/settings/per-diem" testid="settings-per-diem-button" />
        {isOwner && <NavRow icon={PieChart} title={`${platinum.data?.name ?? "Platinum"}'s Take`} subtitle={platinum.data ? `${platinum.data.pct}% of the budget on Budget Jobs` : "Name and percent taken off the top"} to="/settings/platinum" testid="settings-platinum-button" />}
        <NavRow icon={Type} title="Labels" subtitle="Rename Budget Jobs, MY Cut and the location rule" to="/settings/labels" testid="settings-labels-button" />
        <NavRow icon={Mail} title="Email Recipients" subtitle="Who gets the Kastle, Invoice, Approval and Monthly emails" to="/settings/email-recipients" testid="settings-email-recipients-button" />
        <NavRow icon={History} title="Email History" subtitle="Every email sent across all jobs" to="/settings/email-history" testid="settings-email-history-button" />
        <NavRow icon={Flag} title="Job Statuses" subtitle={statuses.data ? `${statuses.data.length} statuses` : "Add or remove statuses"} to="/settings/statuses" testid="settings-statuses-button" />
        <NavRow icon={RotateCcw} title="Recently Deleted" subtitle="Restore jobs and customers deleted in the last 30 days" to="/settings/trash" testid="settings-trash-button" />
        {isOwner && <ReviewAccessRow />}
        {isOwner && <NavRow icon={Trash2} title="Delete All Data" subtitle="Erase every job, customer, team member, part and document" to="/settings/delete-data" testid="settings-delete-all-button" />}
      </div>
      <Btn variant="danger" icon={LogOut} className="mt-8" onClick={logout} data-testid="settings-logout-button">Sign Out</Btn>
    </Page>
  );
}

function Branding() {
  const b = useBranding();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [color, setColor] = useState(DEFAULT_BRAND);
  const [logo, setLogo] = useState(undefined);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (b.data) {
      setName(b.data.company_name);
      setColor(b.data.brand_color || DEFAULT_BRAND);
    }
  }, [b.data]);
  const save = async () => {
    if (!isValidHex(color)) return toast.error("Enter a color like #0057B8");
    setSaving(true);
    try {
      const brand_color = color.toUpperCase() === DEFAULT_BRAND.toUpperCase() ? "" : color.toUpperCase();
      qc.setQueryData(["branding"], await api("/branding", { method: "PUT", body: { company_name: name, logo_path: logo || undefined, remove_logo: logo === null, brand_color } }));
      setLogo(undefined);
      toast.success("Branding saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  const src = typeof logo === "string" ? fileUrl(logo) : logo === undefined && b.data?.has_logo ? logoUrl(b.data) : "/forgeops-icon.png";
  return (
    <div className="max-w-xl">
      <div className="mb-6 flex items-center gap-4">
        <img src={src} alt="logo" data-testid="branding-logo-preview" className="h-20 w-20 rounded-2xl border border-wf-line object-cover" />
        <FilePickButton testid="branding-logo-button" onFiles={async (f) => { try { setLogo(await uploadFile(f[0])); } catch (e) { toast.error(e.message); } }}>Change logo</FilePickButton>
        {(typeof logo === "string" || (logo === undefined && b.data?.has_logo)) && <Btn variant="ghost" onClick={() => setLogo(null)} data-testid="branding-remove-logo">Remove</Btn>}
      </div>
      <Field label="Company Name" testid="branding-name-input" value={name} onChange={setName} placeholder={DEFAULT_NAME} />
      <label className="mb-1.5 block text-[13px] font-semibold text-wf-ink2">Brand Color</label>
      <div className="mb-6 flex items-center gap-3">
        <input type="color" value={isValidHex(color) ? color : DEFAULT_BRAND} onChange={(e) => setColor(e.target.value.toUpperCase())} data-testid="branding-color-picker" className="h-12 w-16 cursor-pointer rounded-lg border border-wf-line" />
        <Field className="mb-0 flex-1" testid="branding-color-input" value={color} onChange={setColor} />
        <Btn variant="ghost" onClick={() => setColor(DEFAULT_BRAND)} data-testid="branding-color-reset">Reset</Btn>
      </div>
      <Btn loading={saving} onClick={save} data-testid="branding-save-button">Save Branding</Btn>
    </div>
  );
}

function SimpleSetting({ qkey, path, fields, toBody, hint }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [qkey], queryFn: () => api(path) });
  const [f, setF] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (q.data) setF(Object.fromEntries(fields.map((x) => [x.key, String(q.data[x.key] ?? "")])));
  }, [q.data]); // eslint-disable-line react-hooks/exhaustive-deps
  if (q.isLoading) return <Loading />;
  const save = async () => {
    const body = toBody(f);
    if (typeof body === "string") return toast.error(body);
    setSaving(true);
    try {
      qc.setQueryData([qkey], await api(path, { method: "PUT", body }));
      toast.success("Saved");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="max-w-xl">
      {fields.map((x) => <Field key={x.key} label={x.label} type={x.type} testid={`${qkey}-${x.key}-input`} value={f[x.key]} placeholder={x.placeholder} onChange={(v) => setF((s) => ({ ...s, [x.key]: v }))} />)}
      {hint && <p className="mb-4 text-sm text-wf-mute">{hint}</p>}
      <Btn loading={saving} onClick={save} data-testid={`${qkey}-save-button`}>Save</Btn>
    </div>
  );
}

function Statuses() {
  const qc = useQueryClient();
  const statuses = useStatuses();
  const [name, setName] = useState("");
  const [del, setDel] = useState(null);
  const add = async () => {
    try {
      await api("/statuses", { method: "POST", body: { name: name.trim() } });
      setName("");
      qc.invalidateQueries({ queryKey: ["statuses"] });
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <div className="max-w-xl">
      <div className="flex items-end gap-2">
        <Field className="flex-1" label="New status" testid="new-status-input" value={name} onChange={setName} placeholder="Waiting on parts" />
        <Btn className="mb-4" onClick={add} disabled={!name.trim()} data-testid="add-status-button">Add</Btn>
      </div>
      <div className="overflow-hidden rounded-2xl border border-wf-line">
        {(statuses.data ?? []).map((s) => (
          <div key={s.id} data-testid={`status-row-${s.id}`} className="flex items-center border-b border-wf-line px-4 py-2 last:border-0">
            <span className="flex-1 font-semibold">{s.name}</span>
            <IconBtn icon={X} tone="danger" onClick={() => setDel(s)} data-testid={`delete-status-${s.id}`} />
          </div>
        ))}
      </div>
      <Confirm open={!!del} title={`Remove "${del?.name}"?`} message="Jobs that use it keep their status." confirmLabel="Remove" onClose={() => setDel(null)}
        onConfirm={async () => { try { await api(`/statuses/${del.id}`, { method: "DELETE" }); qc.invalidateQueries({ queryKey: ["statuses"] }); } catch (e) { toast.error(e.message); } }} />
    </div>
  );
}

const EMAIL_KINDS = [["kastle", "Email Kastle"], ["invoice", "To Be Invoiced"], ["approval", "Email for Approval"], ["monthly", "Monthly Report"]];
function EmailRecipients() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["email-recipients"], queryFn: () => api("/settings/emails") });
  const [draft, setDraft] = useState({});
  if (q.isLoading) return <Loading />;
  const put = async (kind, emails) => {
    try {
      const res = await api(`/settings/emails/${kind}`, { method: "PUT", body: { emails } });
      qc.setQueryData(["email-recipients"], (d) => ({ ...d, [kind]: res.emails }));
      setDraft((s) => ({ ...s, [kind]: "" }));
    } catch (e) {
      toast.error(e.message);
    }
  };
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {EMAIL_KINDS.map(([k, label]) => (
        <Card key={k} testid={`recipients-${k}`}>
          <p className="mb-3 font-bold">{label}</p>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {(q.data?.[k] ?? []).map((e) => (
              <span key={e} className="inline-flex items-center gap-1 rounded-full bg-wf-soft py-1 pl-3 pr-1 text-sm text-wf-softink">{e}
                <button onClick={() => put(k, q.data[k].filter((x) => x !== e))} data-testid={`remove-recipient-${k}`} className="rounded-full p-0.5 hover:bg-white/60"><X size={13} /></button></span>
            ))}
            {!(q.data?.[k] ?? []).length && <span className="text-sm text-wf-mute">No recipients yet</span>}
          </div>
          <div className="flex gap-2">
            <Field className="mb-0 flex-1" type="email" testid={`recipient-input-${k}`} value={draft[k]} onChange={(v) => setDraft((s) => ({ ...s, [k]: v }))} placeholder="name@company.com" />
            <Btn variant="secondary" disabled={!draft[k]?.trim()} onClick={() => put(k, [...(q.data?.[k] ?? []), draft[k].trim()])} data-testid={`add-recipient-${k}`}>Add</Btn>
          </div>
        </Card>
      ))}
    </div>
  );
}

const KIND_LABEL = { kastle: "Email Kastle", invoice: "Invoice summary", approval: "Approval", monthly: "Monthly report" };
function EmailHistory() {
  const q = useQuery({ queryKey: ["email-history"], queryFn: () => api("/email-history") });
  const [kind, setKind] = useState("all");
  if (q.isLoading) return <Loading />;
  if (!q.data?.length) return <Empty icon={Mail} title="No emails sent yet" />;
  const items = q.data.filter((e) => kind === "all" || e.kind === kind);
  return (
    <>
    <div className="mb-4 flex flex-wrap gap-1.5">
      {[["all", "All"], ...Object.entries(KIND_LABEL)].map(([k, l]) => (
        <button key={k} data-testid={`email-history-filter-${k}`} onClick={() => setKind(k)}
          className={cx("rounded-full px-3 py-1.5 text-xs font-bold", kind === k ? "bg-wf-brand text-wf-onbrand" : "bg-wf-s3 text-wf-ink2")}>
          {l} · {k === "all" ? q.data.length : q.data.filter((e) => e.kind === k).length}
        </button>
      ))}
    </div>
    <div className="overflow-hidden rounded-2xl border border-wf-line" data-testid="email-history-list">
      {!items.length && <p className="p-5 text-sm text-wf-mute">No emails of this type yet.</p>}
      {items.map((e, i) => (
        <Link key={i} to={`/work-orders/${e.work_order_id}`} className="flex flex-wrap items-center gap-3 border-b border-wf-line px-5 py-3 last:border-0 hover:bg-wf-s2">
          <span className="rounded-full bg-wf-soft px-2.5 py-0.5 text-xs font-bold text-wf-softink">{KIND_LABEL[e.kind]}</span>
          <span className="min-w-[180px] flex-1"><span className="block truncate font-semibold">{e.customer_name} · {e.job_description}</span><span className="block truncate text-xs text-wf-mute">To {e.to} · by {e.by}</span></span>
          <span className="w-full text-xs text-wf-mute sm:w-auto">{new Date(e.sent_at).toLocaleString()}</span>
          <ArrowUpRight size={15} className="hidden text-wf-line2 sm:block" />
        </Link>
      ))}
    </div>
    </>
  );
}

function Trash() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["trash"], queryFn: () => api("/trash") });
  const [forever, setForever] = useState(null);
  const done = () => {
    qc.invalidateQueries({ queryKey: ["trash"] });
    qc.invalidateQueries({ queryKey: ["work-orders"] });
    qc.invalidateQueries({ queryKey: ["customers"] });
  };
  if (q.isLoading) return <Loading />;
  if (!q.data?.length) return <Empty icon={RotateCcw} title="Nothing deleted recently" subtitle="Deleted jobs and customers show here for 30 days." />;
  return (
    <div className="overflow-hidden rounded-2xl border border-wf-line">
      {q.data.map((t) => (
        <div key={t.id} data-testid={`trash-row-${t.id}`} className="flex flex-wrap items-center gap-3 border-b border-wf-line px-5 py-3 last:border-0">
          <span className="rounded-full bg-wf-s3 px-2.5 py-0.5 text-xs font-bold capitalize">{t.kind}</span>
          <span className="min-w-0 flex-1"><span className="block truncate font-semibold">{t.title}</span>
            <span className="block truncate text-xs text-wf-mute">{t.subtitle} · deleted {new Date(t.deleted_at).toLocaleDateString()} by {t.deleted_by} · until {new Date(t.expires_at).toLocaleDateString()}</span></span>
          <Btn size="sm" variant="secondary" data-testid={`trash-restore-${t.id}`} onClick={async () => { try { await api(`/trash/${t.id}/restore`, { method: "POST" }); done(); toast.success("Restored"); } catch (e) { toast.error(e.message); } }}>Restore</Btn>
          <IconBtn icon={Trash2} tone="danger" onClick={() => setForever(t)} data-testid={`trash-delete-${t.id}`} />
        </div>
      ))}
      <Confirm open={!!forever} title="Delete forever?" onClose={() => setForever(null)}
        onConfirm={async () => { try { await api(`/trash/${forever.id}`, { method: "DELETE" }); done(); } catch (e) { toast.error(e.message); } }} />
    </div>
  );
}

function DeleteData() {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const run = async () => {
    setBusy(true);
    try {
      await api("/admin/delete-all-data", { method: "POST", body: { confirm: typed.trim() } });
      qc.invalidateQueries();
      setTyped("");
      toast.success("All data deleted");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="max-w-xl">
      <p className="mb-4 rounded-xl border border-wf-err bg-red-50 p-4 text-sm text-red-800">This erases every job, customer, team member, part and document for your company. It cannot be undone.</p>
      <Field label="Type DELETE to confirm" testid="delete-data-input" value={typed} onChange={setTyped} placeholder="DELETE" />
      <Btn variant="danger" loading={busy} disabled={typed.trim() !== "DELETE"} onClick={run} data-testid="delete-data-button">Delete All Data</Btn>
    </div>
  );
}

const PAGES = {
  branding: { title: "Company Branding", el: () => <Branding /> },
  "per-diem": {
    title: "Per Diem Amount",
    el: () => <SimpleSetting qkey="per-diem" path="/settings/per-diem" fields={[{ key: "amount", label: "Per Diem Amount ($ per technician per day)", type: "number" }]}
      toBody={(f) => (parseFloat(f.amount) >= 0 ? { amount: parseFloat(f.amount) } : "Enter an amount")} />,
  },
  platinum: {
    title: "Take Off the Top", owner: true,
    el: () => <SimpleSetting qkey="platinum" path="/settings/platinum" fields={[{ key: "name", label: "Name (who takes it)" }, { key: "pct", label: "Percent of the budget (%)", type: "number" }]}
      toBody={(f) => { const pct = parseFloat(f.pct); return pct >= 0 && pct <= 100 ? { pct, name: (f.name ?? "").trim() } : "Enter a percent from 0 to 100"; }} />,
  },
  labels: {
    title: "Labels",
    el: () => <SimpleSetting qkey="labels" path="/settings/labels"
      fields={[{ key: "ny_tab", label: "Tab name (instead of Budget Jobs)", placeholder: DEFAULT_LABELS.ny_tab }, { key: "my_cut", label: "Your cut label (instead of MY Cut)", placeholder: DEFAULT_LABELS.my_cut }, { key: "ny_location", label: "Kastle PM location to include", placeholder: DEFAULT_LABELS.ny_location }]}
      hint={'Jobs show on the Budget Jobs tab when the customer\'s Kastle PM location starts with this word. "NYC" also matches NY and New York.'}
      toBody={(f) => ({ ny_tab: f.ny_tab.trim(), my_cut: f.my_cut.trim(), ny_location: f.ny_location.trim() })} />,
  },
  "email-recipients": { title: "Email Recipients", subtitle: "Changes save automatically", el: () => <EmailRecipients /> },
  "email-history": { title: "Email History", el: () => <EmailHistory /> },
  statuses: { title: "Job Statuses", el: () => <Statuses /> },
  trash: { title: "Recently Deleted", subtitle: "Jobs and customers are kept for 30 days", el: () => <Trash /> },
  "delete-data": { title: "Delete All Data", owner: true, el: () => <DeleteData /> },
};

export function SettingsPage() {
  const { page } = useParams();
  const { isAdmin } = usePerms();
  const p = PAGES[page];
  if (!p || (p.owner && !isAdmin)) return <Navigate to="/settings" replace />;
  return <Page title={p.title} subtitle={p.subtitle} back testid={`settings-${page}-page`}>{p.el()}</Page>;
}

export { emailResultMessage };
